import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Row,
  Col,
  DropdownButton,
  Dropdown,
  Badge,
  Stack,
} from "react-bootstrap";
import "./styles.scss";

const CategoriaSelector = ({ categorias, onCategoriasChange, filtroLimpo }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState([]);
  const [categoriaLoaded, setCategoriaLoaded] = useState(false);
  const [filtro, setFiltro] = useState({ categoriasId: [] });

  const treeRef = useRef(null); // Referência para o contêiner da árvore de categorias
  const itemRefs = useRef({}); // Referência para cada item filtrado

  useEffect(() => {
    // Simula o carregamento das categorias
    setTimeout(() => setCategoriaLoaded(true), 500);
  }, []);

  useEffect(() => {
    // Função para rolar até o item filtrado na árvore de categorias
    const scrollToFilteredItem = () => {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const container = treeRef.current;

      if (container) {
        for (const id in itemRefs.current) {
          if (itemRefs.current.hasOwnProperty(id)) {
            const item = itemRefs.current[id];
            if (
              item &&
              item.innerText.toLowerCase().includes(lowerSearchTerm)
            ) {
              // Ajuste: Rolar o contêiner para o item correspondente
              const itemOffsetTop = item.offsetTop;
              const containerScrollTop = container.scrollTop;
              const containerHeight = container.clientHeight;

              // Se o item estiver fora da área visível, rolamos o contêiner para ele
              if (
                itemOffsetTop < containerScrollTop ||
                itemOffsetTop > containerScrollTop + containerHeight
              ) {
                container.scrollTo({
                  top: itemOffsetTop - container.offsetTop,
                  behavior: "smooth",
                });
              }
              break; // Apenas rola para o primeiro item encontrado
            }
          }
        }
      }
    };

    scrollToFilteredItem();
  }, [searchTerm]);

  const handleLimparCategorias = () => {
    setCategoriasSelecionadas([]);
    setFiltro({ categoriasId: [] });
    setSearchTerm("");
    // Notifique o componente pai se necessário
    onCategoriasChange([]);
  };

  useEffect(() => {
    if (filtroLimpo) {
      handleLimparCategorias();
    }
  }, [filtroLimpo]);

  // Função para alternar a seleção de uma categoria
  const toggleCategoria = (categoria) => {
    const isConteudo = categoria.tipoNome === "Conteúdo";
    if (isConteudo) {
      const index = categoriasSelecionadas.findIndex(
        (c) => c.id === categoria.id
      );

      const getAllChildrenIds = (categoria) => {
        let ids = [categoria.id];
        if (categoria.children && categoria.children.length > 0) {
          categoria.children.forEach((child) => {
            ids = ids.concat(getAllChildrenIds(child));
          });
        }
        return ids;
      };

      const newCategoriasSelecionadas =
        index === -1
          ? [...categoriasSelecionadas, categoria]
          : categoriasSelecionadas.filter((cat) => cat.id !== categoria.id);

      const allIds = newCategoriasSelecionadas.flatMap((cat) =>
        getAllChildrenIds(cat)
      );

      setCategoriasSelecionadas(newCategoriasSelecionadas);
      setFiltro({
        ...filtro,
        categoriasId: allIds,
      });

      // Chama a função de callback para notificar o componente pai
      onCategoriasChange(allIds);
    }
  };

  // Função para filtrar categorias com base no termo de busca
  const filterCategoriasBySearch = (categorias, searchTerm) => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    // Função recursiva para encontrar categorias que correspondem ao termo de busca
    const filterCategorias = (categoria) => {
      if (categoria.nome.toLowerCase().includes(lowerSearchTerm)) {
        return true; // Categoria corresponde ao termo de busca
      }

      // Verifica se algum filho corresponde ao termo de busca
      if (categoria.children) {
        return categoria.children.some(filterCategorias);
      }

      return false;
    };

    return categorias.filter(filterCategorias).map((categoria) => ({
      ...categoria,
      children: categoria.children.filter(filterCategorias),
    }));
  };

  // Função para renderizar as opções de categorias
  const renderOptions = (categorias, level = 0) => {
    return (
      <>
        {categorias.map((categoria) => (
          <React.Fragment key={categoria.id}>
            <Dropdown.Item
              as="button"
              className="option-questao"
              disabled={categoria.tipoNome !== "Conteúdo"}
              onClick={(e) => {
                e.preventDefault();
                toggleCategoria(categoria);
              }}
              style={{ paddingLeft: `${level * 15}px` }}
              ref={(el) => {
                if (el) itemRefs.current[categoria.id] = el;
              }}
            >
              {categoria.nome}
            </Dropdown.Item>
            {categoria.children.length > 0 &&
              renderOptions(categoria.children, level + 1)}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Função para renderizar categorias selecionadas
  const renderSelectedCategorias = () => {
    return categoriasSelecionadas.map((categoria) => (
      <Badge
        key={categoria.id}
        bg="secondary"
        text="white"
        className="me-1 mb-3"
      >
        {categoria.nome}
        <span
          className="ms-2 cursor-pointer"
          onClick={() => toggleCategoria(categoria)}
          role="button"
          aria-label="Remover"
        >
          &times;
        </span>
      </Badge>
    ));
  };

  return (
    <Form.Group as={Row}>
      <Col sm="12">
        {!categoriaLoaded ? (
          <DropdownButton title="Carregando..." disabled />
        ) : categorias.length === 0 ? (
          <DropdownButton title="Nenhuma categoria encontrada" disabled />
        ) : (
          <>
            <DropdownButton
              key="dropdown"
              title="Escolha as categorias"
              drop="end"
            >
              <div className="drop-options">
                <div className="search-container">
                  <Form.Control
                    type="text"
                    placeholder="Buscar categoria..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    className="mb-3"
                    autoFocus
                  />
                </div>
                <div className="options-list" ref={treeRef}>
                  {renderOptions(
                    filterCategoriasBySearch(categorias, searchTerm)
                  )}
                </div>
              </div>
            </DropdownButton>
            {categoriasSelecionadas.length > 0 && (
              <Stack
                direction="horizontal"
                gap={1}
                className="d-flex flex-wrap mt-3"
              >
                {renderSelectedCategorias()}
              </Stack>
            )}
          </>
        )}
      </Col>
    </Form.Group>
  );
};

export default CategoriaSelector;
